import { useState, useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { MarketTrade } from '../api/useMarketTrades';
import { rtdbToMarketTrade } from '../api/sidebarTradeTypes';
import {
    useTradeFiltering,
    type FilteringResult,
    type TradeFilterMode,
    type TradeSortField,
} from './useTradeFiltering';

// ─── What: the trade set the event view is showing ────────────────────────────
//
// This used to live inside LiveTradesSidebar, which made the sidebar the only
// thing that knew which trades were on screen. The chart overlay needs the same
// answer — a mark on the plot that the list does not show, or a filtered-out row
// still drawn on the chart, would be two views disagreeing about one window.
//
// Lifting it also makes the sidebar's existing min-volume control a chart
// declutter control for free, rather than inventing a second threshold.

interface Params {
    liveTrades: Map<string, unknown[]>;
    historicalTrades: MarketTrade[];
    selectedTraderTrades?: MarketTrade[];
    timeRange?: [Date, Date] | null;
    highlightedAccounts?: Set<string> | null;
}

export interface EventTradeSelection extends FilteringResult {
    filter: TradeFilterMode;
    setFilter: Dispatch<SetStateAction<TradeFilterMode>>;
    sortField: TradeSortField;
    setSortField: Dispatch<SetStateAction<TradeSortField>>;
    sortAsc: boolean;
    setSortAsc: Dispatch<SetStateAction<boolean>>;
    minVolume: number;
    setMinVolume: Dispatch<SetStateAction<number>>;
    /** Selection prefixes are stripped; trades carry bare addresses. */
    highlightedAddrs: Set<string>;
    liveSidebarTrades: MarketTrade[];
}

export function useEventTradeSelection({
    liveTrades,
    historicalTrades,
    selectedTraderTrades,
    timeRange,
    highlightedAccounts,
}: Params): EventTradeSelection {
    const [filter, setFilter] = useState<TradeFilterMode>('all');
    const [sortField, setSortField] = useState<TradeSortField>('time');
    const [sortAsc, setSortAsc] = useState(false);
    const [minVolume, setMinVolume] = useState(0);

    // Raw addresses (the prefix is a selection-state detail, not a trade field).
    const highlightedAddrs = useMemo(() => {
        if (!highlightedAccounts) return new Set<string>();
        const addrs = new Set<string>();
        for (const id of highlightedAccounts) {
            addrs.add(id.replace(/^polymarket:/, ''));
        }
        return addrs;
    }, [highlightedAccounts]);

    // Flatten live trades from all markets into MarketTrade[]
    const liveSidebarTrades = useMemo(() => {
        const result: MarketTrade[] = [];
        for (const [, trades] of liveTrades) {
            for (const t of trades) {
                result.push(rtdbToMarketTrade(t as never));
            }
        }
        return result;
    }, [liveTrades]);

    const filtering = useTradeFiltering({
        liveTrades: liveSidebarTrades,
        historicalTrades,
        selectedTraderTrades,
        timeRange,
        filter,
        highlightedAddrs,
        sortField,
        sortAsc,
        minVolume,
    });

    // Memoised: this object lands in EventView's panel useMemo deps, and a fresh
    // identity every render would defeat that memo over a virtualised list.
    return useMemo(
        () => ({
            ...filtering,
            filter,
            setFilter,
            sortField,
            setSortField,
            sortAsc,
            setSortAsc,
            minVolume,
            setMinVolume,
            highlightedAddrs,
            liveSidebarTrades,
        }),
        [
            filtering,
            filter,
            sortField,
            sortAsc,
            minVolume,
            highlightedAddrs,
            liveSidebarTrades,
        ]
    );
}
